import { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { useComposerStore } from '@/stores/composerStore'
import { useWorkshopMode } from './WorkshopModeContext'
import { blockFieldLabel, blockEmoji, blockFieldMeta } from './blockTaxonomy'
import { blockStatus, estimateBlockTokens } from './blockCardStatus'

interface Props {
  category: string
  index: number | null
  ordinal: number
  name: string
  onOpen: () => void
  onRemove?: () => void
  onMoveUp?: () => void
  onMoveDown?: () => void
  /** When true (a just-added block whose focus editor has closed), scroll this
   *  card into view and flash it once, then call onRevealComplete. */
  reveal?: boolean
  onRevealComplete?: () => void
}

const STATUS_COPY = { empty: 'empty', short: 'short', good: 'looks good' } as const
const STATUS_DOT = {
  empty: 'bg-warning', short: 'bg-warning', good: 'bg-success',
} as const

export function BlockCard({ category, index, ordinal, name, onOpen, onRemove, onMoveUp, onMoveDown, reveal, onRevealComplete }: Props) {
  const base = useComposerStore((s) => s.draftBundle ?? s.serverBundle)
  const { mode } = useWorkshopMode()
  const cardRef = useRef<HTMLDivElement>(null)
  const [flashed, setFlashed] = useState(false)
  useEffect(() => {
    if (!reveal) return
    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    // Drive the flash via local state + a one-shot CSS animation, independent
    // of the reveal signal — so clearing the reveal target (below) doesn't cut
    // the animation short.
    setFlashed(true)
    onRevealComplete?.()
  }, [reveal, onRevealComplete])
  const content =
    index !== null
      ? base?.blocks[index]?.content ?? ''
      : base?.blocks.find((b) => b.category === category)?.content ?? ''
  const label = blockFieldLabel(category, ordinal)
  const hint = blockFieldMeta(category, name).hint
  const status = blockStatus(content)
  const snippet = content.trim().replace(/\*\*/g, '')

  return (
    <div ref={cardRef} className={`group relative rounded-xl ${flashed ? 'animate-flash-ring' : ''}`}>
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Edit ${label} block`}
        title={hint}
        className="flex h-full min-h-[128px] w-full flex-col rounded-xl border border-primary/20 bg-primary/5 p-3.5 text-left transition hover:-translate-y-px hover:border-primary hover:shadow-md"
      >
        <span className="mb-2 flex items-center gap-2">
          <span aria-hidden>{blockEmoji(category)}</span>
          <span className="text-sm font-semibold text-foreground">{label}</span>
        </span>
        <span className={`flex-1 text-xs leading-relaxed line-clamp-3 ${snippet ? 'text-foreground' : 'italic text-muted-foreground'}`}>
          {snippet || 'Empty — click to write'}
        </span>
        <span className="mt-2.5 flex items-center gap-2 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span aria-hidden className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT[status]}`} />
            {STATUS_COPY[status]}
          </span>
          <span className="ml-auto tabular-nums">{content.length} ch · ≈{estimateBlockTokens(content)}t</span>
        </span>
      </button>
      {mode === 'advanced' && index !== null && (onRemove || onMoveUp || onMoveDown) && (
        <div className="absolute right-2 top-2 flex gap-0.5 opacity-0 transition group-hover:opacity-100">
          {onMoveUp && (
            <button
              type="button"
              aria-label={`Move ${label} up`}
              onClick={onMoveUp}
              className="rounded-sm p-1 text-muted-foreground hover:bg-muted-foreground/10"
            >
              ↑
            </button>
          )}
          {onMoveDown && (
            <button
              type="button"
              aria-label={`Move ${label} down`}
              onClick={onMoveDown}
              className="rounded-sm p-1 text-muted-foreground hover:bg-muted-foreground/10"
            >
              ↓
            </button>
          )}
          {onRemove && (
            <button
              type="button"
              aria-label={`Remove ${label}`}
              onClick={onRemove}
              className="rounded-sm p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
    </div>
  )
}
